import {
    Insights as ContextIcon,
    InfoOutlined,
    Psychology as LLMIcon,
    LabelImportant as PriorityIcon,
    BarChart as ScoreIcon,
} from "@mui/icons-material";
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    IconButton,
    Table,
    TableCell,
    TableRow,
    Typography
} from "@mui/material";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { useIssueRecommendation } from "../../../hooks/process/useIssueRecommendation";
import CommonPopper from "../commonPopper";
import { isEmpty } from "lodash";
import ReactMarkdownStyled from "../reactMarkdownStyled";

interface RecommendationCardProps {
    issueId?: string;
}

const RecommendationCard = ({ issueId }: RecommendationCardProps) => {
    const { loading, recommendation } = useIssueRecommendation(issueId);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [activeContent, setActiveContent] = useState<string>("");

    const handlePopperOpen = (event: React.MouseEvent<HTMLElement>, content: string) => {
        setAnchorEl(event.currentTarget);
        setActiveContent(content);
    };

    const handlePopperClose = () => {
        setAnchorEl(null);
        setActiveContent("");
    };

    const getSeverityColor = (severity: string) => {
        if (severity === "critical") return "error.dark";
        if (severity === "high" || severity === "error") return "error.main";
        if (severity === "medium" || severity === "warning") return "warning.main";
        if (severity === "low" || severity === "info") return "info.main";
        return "information.main";
    };

    const getScoreSeverity = (score: number) => {
        if (score >= 85) return "critical";
        if (score >= 60) return "high";
        if (score >= 45) return "medium";
        if (score >= 25) return "low";
        return "information";
    };

    const getSegregateSeverity = (segregate: string) => {
        switch (segregate) {
            case "URGENT":
                return "critical";
            case "HIGH PRIORITY":
                return "high";
            case "BACKLOG":
                return "warning";
            default:
                return "info";
        }
    };

    const rows = recommendation
        ? [
            {
                label: "Risk Score",
                value: `${recommendation?.score}/100`,
                icon: <ScoreIcon fontSize="small" />,
                severity: getScoreSeverity(recommendation?.score ?? 0),
                reason: recommendation?.scoreReason,
                explanation: `**Risk Score** represents the AI's numerical assessment of how risky the issue is on a scale of 0 to 100.

The score is calculated based on:
- Severity of the issue (e.g., SQL Injection, Privilege Escalation)
- Exploitability (how easily the issue can be triggered)
- Impact Scope (system-wide vs local)
- Misconfiguration or compliance violations
- Frequency and historical context`
            },
            {
                label: "Technical Assessment",
                value: recommendation?.isImportantLLMJudgement ? "Important" : "Not important",
                icon: <LLMIcon fontSize="small" />,
                severity: recommendation?.isImportantLLMJudgement
                    ? getSegregateSeverity(recommendation?.segregate ?? "")
                    : "low",
                reason: recommendation?.isImportantLLMJudgementReason,
                explanation: `**Technical Assessment** reflects what the AI believes about the importance of this issue purely based on technical content.

This assessment does **not** consider historical patterns or user feedback. It is derived from:
- Industry best practices
- Threat modeling
- Known vulnerability impacts
- Exploit feasibility`
            },
            {
                label: "Contextual Assessment",
                value: recommendation?.isImportantContextual ? "Important" : "Not important",
                icon: <ContextIcon fontSize="small" />,
                severity: recommendation?.isImportantContextual
                    ? getSegregateSeverity(recommendation?.segregate ?? "")
                    : "low",
                reason: recommendation?.isImportantContextualReason,
                explanation: `**Contextual Assessment** combines technical severity with past usage data and project-specific context.

This includes:
- System-assigned risk level (e.g., high/medium)
- How similar issues were treated by users (e.g., marked as important or not)
- Frequency and patterns in past reports
- Prioritization trends across your codebase`
            },
            {
                label: "Action Urgency",
                value: recommendation?.segregate
                    ?.replace("URGENT", "Urgent")
                    ?.replace("HIGH PRIORITY", "High priority")
                    ?.replace("BACKLOG", "Backlog")
                    ?.replace("NOT IMPORTANT", "Not important"),
                icon: <PriorityIcon fontSize="small" />,
                severity: getSegregateSeverity(recommendation?.segregate ?? ""),
                reason: recommendation?.segregateReason,
                explanation: `**Action Urgency** describes how quickly this issue should be addressed.

AI evaluates:
- Whether the issue needs immediate attention
- The potential blast radius and business impact
- Overall urgency across all issues in the run

Classifications:
- \`Urgent\`: must be fixed now
- \`High priority\`: should be addressed this sprint or next
- \`Backlog\`: can be delayed until this month
- \`Not important\`: low risk, likely to be ignored`
            },
        ]
        : [];

    

    return (
        <Box>
            {loading ? (
                <CircularProgress size="1em" />
            ) : !isEmpty(recommendation) && (
                <>
                    <Box sx={{ pl: 1 }}>
                        <Typography variant="overline">AI Analysis</Typography>
                    </Box>
                    <Box sx={{
                        backgroundColor: "background.default",
                        width: "100%",

                        p: 0,
                        my: 2,
                    }}>
                        <Table size="small">
                            {rows.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell  sx={{ borderBottom: index === (rows?.length - 1) ? 'none' : undefined }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {row.icon}
                                            <Typography variant="subtitle2" fontWeight={600}>
                                                {row.label}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: index === (rows?.length - 1) ? 'none' : undefined }}>
                                        <Typography color={getSeverityColor(row.severity)} fontWeight={600}>
                                            {row.value}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: index === (rows?.length - 1) ? 'none' : undefined }}>
                                        {row.reason && (
                                            <CommonPopper
                                                elevatePopper
                                                placement="bottom-start"
                                                onClose={handlePopperClose}
                                                child={
                                                    <Card elevation={1} sx={{ width: "30em", p: 1, maxHeight: "45vh", overflowY: "auto" }}>
                                                        <CardContent>

                                                            <Typography variant="h5" fontWeight={600} mt={1}>
                                                                Reasoning
                                                            </Typography>
                                                            <ReactMarkdownStyled markdown={row.reason} />
                                                            <br />
                                                            <Typography variant="h5" fontWeight={600}>
                                                                How AI arrived at this conclusion
                                                            </Typography>
                                                            <ReactMarkdownStyled markdown={row.explanation} />
                                                        </CardContent>
                                                    </Card>
                                                }
                                                trigger={
                                                    <IconButton
                                                        onClick={(e) => handlePopperOpen(e, row.reason!)}
                                                        size="small"
                                                        sx={{ p: 0.5 }}
                                                    >
                                                        <InfoOutlined fontSize="small" />
                                                    </IconButton>
                                                }
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </Table>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default RecommendationCard;
